import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Tag, Mail, Trash2, Lock, Shield, ChefHat, Eye, EyeOff, Copy } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const KitchenPinManager = () => {
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    axios.get(`${API}/admin/kitchen-pin`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPin(res.data.pin))
      .catch(() => setPin("1234"));
  }, []);

  const savePin = async () => {
    if (!newPin || newPin.length < 4) { toast.error("PIN-kode ma vaere minst 4 siffer"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/kitchen-pin`, { pin: newPin }, { headers: { Authorization: `Bearer ${token}` } });
      setPin(newPin);
      setNewPin("");
      toast.success("Kjokken PIN-kode oppdatert!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke oppdatere PIN");
    }
    setSaving(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(`${window.location.origin}/kjokkenet/login`);
    toast.success("URL kopiert!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: '#f4e7d8' }}>
        <ChefHat className="h-8 w-8 flex-shrink-0" style={{ color: '#721111' }} />
        <div className="flex-1">
          <p className="font-semibold" style={{ color: '#2d1810' }}>Gjeldende PIN-kode:</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-2xl font-mono tracking-widest" style={{ color: '#721111' }} data-testid="current-kitchen-pin">
              {showPin ? pin : "****"}
            </span>
            <button onClick={() => setShowPin(!showPin)} style={{ color: '#8b4513' }}>
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: '#fef3c7', border: '1px solid #f59e0b' }}>
        <span className="text-sm" style={{ color: '#92400e' }}>Kjokken-URL: <strong>{window.location.origin}/kjokkenet/login</strong></span>
        <button onClick={copyUrl} className="ml-auto" style={{ color: '#92400e' }}><Copy className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-2">
        <Input
          type="text"
          value={newPin}
          onChange={(e) => setNewPin(e.target.value)}
          placeholder="Ny PIN-kode (minst 4 siffer)"
          className="indian-input"
          data-testid="new-kitchen-pin-input"
        />
        <Button onClick={savePin} disabled={saving} className="btn-maroon rounded-full px-6" data-testid="save-kitchen-pin-btn">
          {saving ? "Lagrer..." : "Lagre"}
        </Button>
      </div>
    </div>
  );
};

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [emailSubscribers, setEmailSubscribers] = useState([]);
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [usernameData, setUsernameData] = useState({ current_password: "", new_username: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingUsername, setChangingUsername] = useState(false);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [recentFailed, setRecentFailed] = useState(0);
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: 0,
    minimum_order: 0,
    max_uses: null,
    valid_for: "",
    expires_at: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsRes, couponsRes, subscribersRes, logsRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/coupons`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/email-subscribers`, { headers }).catch(() => ({ data: { subscribers: [] } })),
        axios.get(`${API}/admin/security-logs`, { headers }).catch(() => ({ data: { logs: [], recent_failed_24h: 0 } })),
      ]);
      setSettings(settingsRes.data);
      setCoupons(couponsRes.data);
      setEmailSubscribers(subscribersRes.data?.subscribers || []);
      setSecurityLogs(logsRes.data?.logs || []);
      setRecentFailed(logsRes.data?.recent_failed_24h || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Innstillinger oppdatert!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Kunne ikke lagre innstillinger");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/coupons`, {
        ...newCoupon,
        code: newCoupon.code.toUpperCase(),
        valid_for: newCoupon.valid_for || null,
        max_uses: newCoupon.max_uses || null,
        expires_at: newCoupon.expires_at || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Kupong ${newCoupon.code.toUpperCase()} opprettet!`);
      setNewCoupon({
        code: "",
        discount_type: "percentage",
        discount_value: 0,
        minimum_order: 0,
        max_uses: null,
        valid_for: "",
        expires_at: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating coupon:", error);
      toast.error("Kunne ikke opprette kupong");
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Slett kupong ${code}?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/coupons/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kupong slettet");
      fetchData();
    } catch (error) {
      console.error("Error deleting coupon:", error);
      toast.error("Kunne ikke slette kupong");
    }
  };

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Fjerne ${email} fra e-postlisten?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/email-subscribers/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Abonnent fjernet");
      fetchData();
    } catch (error) {
      toast.error("Kunne ikke fjerne abonnent");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div 
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
              style={{ borderColor: '#721111', borderRightColor: 'transparent' }}
            ></div>
            <p className="mt-4" style={{ color: '#4a3728' }}>Laster...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-12 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Admin</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
            style={{ color: '#d4af37' }}
            data-testid="admin-settings-title"
          >
            Innstillinger
          </motion.h1>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList 
            className="p-1 rounded-xl"
            style={{ background: '#ede1c8', border: '2px solid #b27f47' }}
          >
            <TabsTrigger 
              value="settings" 
              className="data-[state=active]:bg-white rounded-lg px-4 py-2"
              style={{ color: '#2d1810' }}
            >
              <Settings className="h-4 w-4 mr-2" />
              Restaurant
            </TabsTrigger>
            <TabsTrigger 
              value="email-subscribers" 
              className="data-[state=active]:bg-white rounded-lg px-4 py-2"
              style={{ color: '#2d1810' }}
            >
              <Mail className="h-4 w-4 mr-2" />
              E-postliste
            </TabsTrigger>
            <TabsTrigger 
              value="coupons" 
              className="data-[state=active]:bg-white rounded-lg px-4 py-2"
              style={{ color: '#2d1810' }}
            >
              <Tag className="h-4 w-4 mr-2" />
              Kuponger
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="data-[state=active]:bg-white rounded-lg px-4 py-2"
              style={{ color: '#2d1810' }}
            >
              <Lock className="h-4 w-4 mr-2" />
              Sikkerhet
            </TabsTrigger>
            <TabsTrigger 
              value="security-logs" 
              className="data-[state=active]:bg-white rounded-lg px-4 py-2 relative"
              style={{ color: '#2d1810' }}
            >
              <Shield className="h-4 w-4 mr-2" />
              Sikkerhetslogg
              {recentFailed > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {recentFailed}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Restaurant Settings */}
          <TabsContent value="settings">
            <div 
              className="bg-white rounded-xl shadow-lg p-8"
              style={{ border: '2px solid #b27f47' }}
            >
              <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                Restaurantinformasjon
              </h2>
              <form onSubmit={handleSaveSettings} className="space-y-6">
                <div>
                  <Label style={{ color: '#2d1810' }}>Restaurantnavn</Label>
                  <Input
                    value={settings.restaurant_name || ""}
                    onChange={(e) => setSettings({ ...settings, restaurant_name: e.target.value })}
                    className="indian-input mt-1"
                    data-testid="restaurant-name-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Telefon</Label>
                    <Input
                      value={settings.phone || ""}
                      onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                      className="indian-input mt-1"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>E-post</Label>
                    <Input
                      type="email"
                      value={settings.email || ""}
                      onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                      className="indian-input mt-1"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Adresse</Label>
                  <Input
                    value={settings.address || ""}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    className="indian-input mt-1"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Facebook URL</Label>
                    <Input
                      value={settings.facebook_url || ""}
                      onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                      className="indian-input mt-1"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Instagram URL</Label>
                    <Input
                      value={settings.instagram_url || ""}
                      onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                      className="indian-input mt-1"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>TripAdvisor URL</Label>
                    <Input
                      value={settings.tripadvisor_url || ""}
                      onChange={(e) => setSettings({ ...settings, tripadvisor_url: e.target.value })}
                      className="indian-input mt-1"
                    />
                  </div>
                </div>

                <div className="pt-6" style={{ borderTop: '2px solid #d4af37' }}>
                  <h3 className="text-xl font-serif-display font-semibold mb-4" style={{ color: '#2d1810' }}>
                    Travelt-modus
                  </h3>
                  <p className="text-sm mb-4" style={{ color: '#4a3728' }}>
                    Aktiver når restauranten er veldig travelt for å advare kunder om lengre ventetider
                  </p>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="busy_mode"
                      checked={settings.busy_mode_enabled || false}
                      onChange={(e) => setSettings({ ...settings, busy_mode_enabled: e.target.checked })}
                      className="w-5 h-5 rounded"
                      style={{ accentColor: '#721111' }}
                      data-testid="busy-mode-toggle"
                    />
                    <Label htmlFor="busy_mode" className="cursor-pointer font-semibold" style={{ color: '#2d1810' }}>
                      {settings.busy_mode_enabled ? "🔴 Travelt-modus PÅ" : "🟢 Travelt-modus AV"}
                    </Label>
                  </div>

                  <div>
                    <Label style={{ color: '#2d1810' }}>Melding ved Travelt-modus</Label>
                    <Textarea
                      value={settings.busy_mode_message || ""}
                      onChange={(e) => setSettings({ ...settings, busy_mode_message: e.target.value })}
                      rows={3}
                      placeholder="Vi er veldig travle akkurat nå. Bestillinger kan ta 1+ time."
                      className="indian-input mt-1"
                      data-testid="busy-mode-message-input"
                    />
                    <p className="text-xs mt-1" style={{ color: '#8b4513' }}>
                      Denne meldingen vises til kunder før de legger inn en bestilling
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-full font-bold px-10 py-6 btn-maroon transition-transform hover:scale-105"
                  data-testid="save-settings-button"
                >
                  {saving ? "Lagrer..." : "Lagre Innstillinger"}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Email Subscribers */}
          <TabsContent value="email-subscribers">
            <div 
              className="bg-white rounded-xl shadow-lg p-8"
              style={{ border: '2px solid #b27f47' }}
            >
              <h2 className="text-2xl font-serif-display font-semibold mb-2" style={{ color: '#2d1810' }}>
                E-postabonnenter
              </h2>
              <p className="text-sm mb-6" style={{ color: '#8b4513' }}>
                Kunder som har samtykket til e-postmarkedsforing ved bestilling eller reservasjon
              </p>

              <div 
                className="p-4 rounded-lg flex items-center gap-3 mb-6"
                style={{ background: '#dcfce7', border: '1px solid #22c55e' }}
                data-testid="email-subscribers-count"
              >
                <Mail className="h-5 w-5" style={{ color: '#166534' }} />
                <span style={{ color: '#166534' }}>
                  {emailSubscribers.length} abonnent{emailSubscribers.length !== 1 ? 'er' : ''}
                </span>
              </div>

              {emailSubscribers.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#4a3728' }}>
                  Ingen e-postabonnenter enna. Kunder som sier "Ja, gjerne!" til e-posttilbud ved bestilling vil vises her.
                </p>
              ) : (
                <div className="rounded-xl overflow-hidden" style={{ border: '2px solid #d4af37' }}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#f4e7d8', borderBottom: '2px solid #b27f47' }}>
                        <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>E-post</th>
                        <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Kilde</th>
                        <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Samtykket</th>
                        <th className="text-right py-3 px-4" style={{ color: '#2d1810' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailSubscribers.map((sub) => (
                        <tr key={sub.email} style={{ borderBottom: '1px solid #d4af37' }}>
                          <td className="py-3 px-4" style={{ color: '#4a3728' }}>{sub.email}</td>
                          <td className="py-3 px-4">
                            <span 
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                              style={{ 
                                background: sub.source === 'reservasjon' ? '#dbeafe' : '#dcfce7',
                                color: sub.source === 'reservasjon' ? '#1e40af' : '#166534'
                              }}
                            >
                              {sub.source === 'reservasjon' ? 'Reservasjon' : sub.source === 'bestilling' ? 'Bestilling' : 'Ukjent'}
                            </span>
                          </td>
                          <td className="py-3 px-4" style={{ color: '#8b4513' }}>
                            {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => handleDeleteSubscriber(sub.email)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-subscriber-${sub.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Email notifications config */}
              <div className="mt-8 pt-8" style={{ borderTop: '2px solid #d4af37' }}>
                <h3 className="text-xl font-serif-display font-semibold mb-4" style={{ color: '#2d1810' }}>
                  E-postvarsler
                </h3>
                <p className="text-sm mb-4" style={{ color: '#8b4513' }}>
                  Motta e-post nar nye bestillinger kommer inn
                </p>
                <div className="space-y-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Varsel E-post</Label>
                    <Input
                      type="email"
                      value={settings.notification_email || ""}
                      onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                      placeholder="din@epost.no"
                      className="indian-input mt-1"
                      data-testid="notification-email-input"
                    />
                    <p className="text-xs mt-1" style={{ color: '#8b4513' }}>
                      E-postadressen som mottar bestillingsvarsler
                    </p>
                  </div>
                  <div 
                    className="p-4 rounded-lg flex items-center gap-3"
                    style={{ 
                      background: settings.notification_email ? '#dcfce7' : '#fef3c7',
                      border: `1px solid ${settings.notification_email ? '#22c55e' : '#f59e0b'}`
                    }}
                  >
                    <div 
                      className={`w-3 h-3 rounded-full ${settings.notification_email ? 'bg-green-500' : 'bg-amber-500'}`}
                    ></div>
                    <span style={{ color: settings.notification_email ? '#166534' : '#92400e' }}>
                      {settings.notification_email 
                        ? 'E-postvarsler er konfigurert'
                        : 'Fyll inn e-postadresse for a aktivere varsler'
                      }
                    </span>
                  </div>
                  <Button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="rounded-full font-bold px-10 py-6 btn-maroon transition-transform hover:scale-105"
                    data-testid="save-email-settings-button"
                  >
                    {saving ? "Lagrer..." : "Lagre E-postinnstillinger"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Coupons */}
          <TabsContent value="coupons">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create Coupon */}
              <div 
                className="bg-white rounded-xl shadow-lg p-8"
                style={{ border: '2px solid #d4af37' }}
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                  Opprett Ny Kupong
                </h2>
                <form onSubmit={handleCreateCoupon} className="space-y-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Kupongkode</Label>
                    <Input
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                      placeholder="HENT15"
                      required
                      className="indian-input mt-1"
                      data-testid="coupon-code-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label style={{ color: '#2d1810' }}>Rabattype</Label>
                      <select
                        value={newCoupon.discount_type}
                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                        className="indian-input w-full mt-1"
                      >
                        <option value="percentage">Prosent %</option>
                        <option value="fixed">Fast Beløp NOK</option>
                      </select>
                    </div>
                    <div>
                      <Label style={{ color: '#2d1810' }}>Rabattverdi</Label>
                      <Input
                        type="number"
                        value={newCoupon.discount_value}
                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: parseFloat(e.target.value) })}
                        min="0"
                        required
                        className="indian-input mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label style={{ color: '#2d1810' }}>Minimumsordre (NOK)</Label>
                      <Input
                        type="number"
                        value={newCoupon.minimum_order}
                        onChange={(e) => setNewCoupon({ ...newCoupon, minimum_order: parseFloat(e.target.value) })}
                        min="0"
                        className="indian-input mt-1"
                      />
                    </div>
                    <div>
                      <Label style={{ color: '#2d1810' }}>Maks Bruk (Valgfritt)</Label>
                      <Input
                        type="number"
                        value={newCoupon.max_uses || ""}
                        onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                        min="1"
                        placeholder="Ubegrenset"
                        className="indian-input mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label style={{ color: '#2d1810' }}>Gyldig For</Label>
                    <select
                      value={newCoupon.valid_for}
                      onChange={(e) => setNewCoupon({ ...newCoupon, valid_for: e.target.value })}
                      className="indian-input w-full mt-1"
                    >
                      <option value="">Kun Henting</option>
                      <option value="pickup">Kun Henting</option>
                    </select>
                  </div>

                  <div>
                    <Label style={{ color: '#2d1810' }}>Utløper (Valgfritt)</Label>
                    <Input
                      type="datetime-local"
                      value={newCoupon.expires_at}
                      onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                      className="indian-input mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105"
                    data-testid="create-coupon-button"
                  >
                    Opprett Kupong
                  </Button>
                </form>
              </div>

              {/* Active Coupons */}
              <div 
                className="bg-white rounded-xl shadow-lg p-8"
                style={{ border: '2px solid #b27f47' }}
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                  Aktive Kuponger ({coupons.filter(c => c.is_active).length})
                </h2>
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {coupons.filter(c => c.is_active).length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#4a3728' }}>Ingen aktive kuponger</p>
                  ) : (
                    coupons.filter(c => c.is_active).map((coupon) => (
                      <div 
                        key={coupon.code} 
                        className="rounded-xl p-4"
                        style={{ border: '2px solid #d4af37', background: '#f4e7d8' }}
                        data-testid={`coupon-${coupon.code}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-lg font-bold" style={{ color: '#721111' }}>{coupon.code}</h3>
                            <p className="text-sm" style={{ color: '#4a3728' }}>
                              {coupon.discount_type === "percentage"
                                ? `${coupon.discount_value}% rabatt`
                                : `${coupon.discount_value} NOK rabatt`}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteCoupon(coupon.code)}
                            style={{ color: '#ef4444' }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="text-xs space-y-1" style={{ color: '#8b4513' }}>
                          {coupon.minimum_order > 0 && <p>Min ordre: {coupon.minimum_order} NOK</p>}
                          {coupon.max_uses && (
                            <p>Brukt: {coupon.current_uses}/{coupon.max_uses}</p>
                          )}
                          {coupon.valid_for && <p>Gyldig for: kun {coupon.valid_for}</p>}
                          {coupon.expires_at && (
                            <p>Utløper: {new Date(coupon.expires_at).toLocaleDateString('nb-NO')}</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Security - Change Username & Password */}
          <TabsContent value="security">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Change Password */}
              <div 
                className="bg-white rounded-xl shadow-lg p-8"
                style={{ border: '2px solid #b27f47' }}
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-2" style={{ color: '#2d1810' }}>
                  Endre Passord
                </h2>
                <p className="text-sm mb-6" style={{ color: '#8b4513' }}>
                  Velg et sterkt passord med minst 6 tegn
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (passwordData.new_password !== passwordData.confirm_password) {
                    toast.error("Passordene er ikke like");
                    return;
                  }
                  setChangingPassword(true);
                  try {
                    const token = localStorage.getItem("admin_token");
                    await axios.post(`${API}/admin/change-password`, {
                      current_password: passwordData.current_password,
                      new_password: passwordData.new_password
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success("Passord endret!");
                    setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Kunne ikke endre passord");
                  } finally {
                    setChangingPassword(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Nåværende passord</Label>
                    <Input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                      className="indian-input mt-1"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Nytt passord</Label>
                    <Input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      required
                      minLength={6}
                      className="indian-input mt-1"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Bekreft nytt passord</Label>
                    <Input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      required
                      minLength={6}
                      className="indian-input mt-1"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105"
                    data-testid="change-password-button"
                  >
                    {changingPassword ? "Endrer..." : "Endre Passord"}
                  </Button>
                </form>
              </div>

              {/* Change Username */}
              <div 
                className="bg-white rounded-xl shadow-lg p-8"
                style={{ border: '2px solid #b27f47' }}
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-2" style={{ color: '#2d1810' }}>
                  Endre Brukernavn
                </h2>
                <p className="text-sm mb-6" style={{ color: '#8b4513' }}>
                  Brukernavn brukes for innlogging til admin-panelet
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setChangingUsername(true);
                  try {
                    const token = localStorage.getItem("admin_token");
                    const res = await axios.post(`${API}/admin/change-username`, {
                      current_password: usernameData.current_password,
                      new_username: usernameData.new_username
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    localStorage.setItem("admin_token", res.data.new_token);
                    toast.success(`Brukernavn endret til "${res.data.username}"`);
                    setUsernameData({ current_password: "", new_username: "" });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Kunne ikke endre brukernavn");
                  } finally {
                    setChangingUsername(false);
                  }
                }} className="space-y-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Nytt brukernavn</Label>
                    <Input
                      type="text"
                      value={usernameData.new_username}
                      onChange={(e) => setUsernameData({ ...usernameData, new_username: e.target.value })}
                      required
                      minLength={3}
                      className="indian-input mt-1"
                      data-testid="new-username-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Bekreft med passord</Label>
                    <Input
                      type="password"
                      value={usernameData.current_password}
                      onChange={(e) => setUsernameData({ ...usernameData, current_password: e.target.value })}
                      required
                      className="indian-input mt-1"
                      data-testid="username-confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={changingUsername}
                    className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105"
                    data-testid="change-username-button"
                  >
                    {changingUsername ? "Endrer..." : "Endre Brukernavn"}
                  </Button>
                </form>
              </div>

              {/* Kitchen PIN */}
              <div 
                className="bg-white rounded-xl shadow-lg p-8 lg:col-span-2"
                style={{ border: '2px solid #b27f47' }}
              >
                <h2 className="text-2xl font-serif-display font-semibold mb-2" style={{ color: '#2d1810' }}>
                  Kjokken PIN-kode
                </h2>
                <p className="text-sm mb-4" style={{ color: '#8b4513' }}>
                  PIN-kode for kjokkenansatte. Del denne med personalet. Kjokken-URL: <strong>/kjokkenet/login</strong>
                </p>
                <KitchenPinManager />
              </div>
            </div>
          </TabsContent>

          {/* Security Logs */}
          <TabsContent value="security-logs">
            <div 
              className="bg-white rounded-xl shadow-lg p-8"
              style={{ border: '2px solid #b27f47' }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                    Sikkerhetslogg
                  </h2>
                  <p className="text-sm mt-1" style={{ color: '#8b4513' }}>
                    Oversikt over innloggingsforsok og sikkerhetsendringer
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    if (!window.confirm("Slette alle loggoppforinger?")) return;
                    try {
                      const token = localStorage.getItem("admin_token");
                      await axios.delete(`${API}/admin/security-logs`, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Logg slettet");
                      fetchData();
                    } catch {
                      toast.error("Kunne ikke slette logg");
                    }
                  }}
                  variant="outline"
                  className="rounded-full text-sm"
                  style={{ borderColor: '#b27f47', color: '#721111' }}
                  data-testid="clear-security-logs-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Slett Logg
                </Button>
              </div>

              {/* Alert banner for recent failed attempts */}
              {recentFailed > 0 && (
                <div 
                  className="p-4 rounded-lg flex items-center gap-3 mb-6"
                  style={{ background: '#fef2f2', border: '2px solid #ef4444' }}
                  data-testid="failed-attempts-warning"
                >
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">
                      {recentFailed} mislykkede forsok siste 24 timer
                    </p>
                    <p className="text-sm text-red-600">
                      Noen har provet a logge inn med feil brukernavn eller passord
                    </p>
                  </div>
                </div>
              )}

              {recentFailed === 0 && (
                <div 
                  className="p-4 rounded-lg flex items-center gap-3 mb-6"
                  style={{ background: '#dcfce7', border: '1px solid #22c55e' }}
                  data-testid="no-failed-attempts"
                >
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-green-800">Ingen mislykkede forsok siste 24 timer</span>
                </div>
              )}

              {/* Log table */}
              {securityLogs.length === 0 ? (
                <p className="text-center py-8" style={{ color: '#4a3728' }}>
                  Ingen loggoppforinger enna.
                </p>
              ) : (
                <div className="rounded-xl overflow-hidden" style={{ border: '2px solid #d4af37' }}>
                  <div className="max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0">
                        <tr style={{ background: '#f4e7d8', borderBottom: '2px solid #b27f47' }}>
                          <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Tidspunkt</th>
                          <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Hendelse</th>
                          <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Brukernavn</th>
                          <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>IP-adresse</th>
                          <th className="text-center py-3 px-4" style={{ color: '#2d1810' }}>Status</th>
                          <th className="text-left py-3 px-4" style={{ color: '#2d1810' }}>Detaljer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {securityLogs.map((log, idx) => {
                          const eventLabels = {
                            login_success: "Innlogging",
                            login_failed: "Mislykket innlogging",
                            password_changed: "Passord endret",
                            password_change_failed: "Passordendring feilet",
                            username_changed: "Brukernavn endret",
                            username_change_failed: "Brukernavnendring feilet",
                          };
                          return (
                            <tr 
                              key={idx} 
                              style={{ 
                                borderBottom: '1px solid #d4af37',
                                background: log.success ? 'transparent' : '#fef2f2'
                              }}
                              data-testid={`security-log-row-${idx}`}
                            >
                              <td className="py-3 px-4" style={{ color: '#4a3728' }}>
                                {log.timestamp ? new Date(log.timestamp).toLocaleString('nb-NO', { 
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit' 
                                }) : '-'}
                              </td>
                              <td className="py-3 px-4 font-medium" style={{ color: log.success ? '#166534' : '#991b1b' }}>
                                {eventLabels[log.event_type] || log.event_type}
                              </td>
                              <td className="py-3 px-4" style={{ color: '#4a3728' }}>
                                {log.username || '-'}
                              </td>
                              <td className="py-3 px-4 font-mono text-xs" style={{ color: '#8b4513' }}>
                                {log.ip || '-'}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {log.success ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    OK
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Feilet
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-xs" style={{ color: '#8b4513' }}>
                                {log.details || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="text-xs mt-4" style={{ color: '#8b4513' }}>
                Viser de siste 200 hendelsene. Eldre hendelser slettes automatisk.
              </p>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminSettingsPage;
